import axios from 'axios';

import { getConfiguration } from 'service/ConfigAPI';

const tempTestUrl = 'https://testapi.cloudconnect.ml:8080';

const netsRequest = axios.create({
  baseURL: `${tempTestUrl}/v1/`,
  timeout: 180000,
});

const getConfigsFromSStorage = () => {
  const data = [];
  data.push(sessionStorage.getItem('nets-token'));
  data.push(sessionStorage.getItem('nets-terminal') || '84000227');
  return data;
};

export const getNetsConfig = async (level, level_id) => {
  try {
    let configValues = {};
    const configuration = await getConfiguration({
      level,
      level_id,
      name: 'netsCloud',
      type: 'payment',
    });

    try {
      configValues = JSON.parse(configuration.attributeValue);
    } catch (err) {
      // Default value is fine if it fails to parse
    }

    return configValues;
  } catch (err) {
    return {};
  }
};

const login = (username, password) =>
  netsRequest
    .post(`login`, { username, password })
    .then((data) => data)
    .catch((e) => {
      throw e;
    });

export const netsAuth = async () => {
  try {
    const posID = localStorage.getItem();
    const warehouseID = localStorage.getItem();
    const config = await getNetsConfig(warehouseID, posID);
    if (config && config.username && config.password) {
      await login(config.username, config.password)
        .then((data) => {
          const { token } = data.data;
          if (token) sessionStorage.setItem(`nets-token`, token);
          if (config.terminal)
            sessionStorage.setItem(`nets-terminal`, config.terminal);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  } catch (err) {
    console.error(err);
  }
};

/**
 * Function that checks if the authorization token has expired (statusCode === 401)
 * and sends request for new token. Once the token has been received the request is resent
 * with the same parameters
 *
 * @param {T} request
 * @returns {T}
 * @template T
 */
export const withAuthCheck =
  (request) =>
  async (...params) => {
    let expiredToken = false;
    let data = null;
    let error = null;

    try {
      data = await request(...params);
    } catch (err) {
      if (err.response.status === 401) {
        expiredToken = true;
      } else {
        throw err;
      }
    }

    if (expiredToken) {
      try {
        await netsAuth();
        data = await request(...params);
      } catch (err) {
        error = err;
      }
    }

    if (error) {
      throw new Error(error);
    }
    return data;
  };

const getTerminals = () => {
  const [token] = getConfigsFromSStorage();
  return netsRequest
    .get('terminal', {
      headers: {
        Authorization: `bearer ${token}`,
      },
    })
    .then((data) => data)
    .catch((e) => {
      throw e;
    });
};

const getTerminalInfo = (terminalId) => {
  const [token, terminalID] = getConfigsFromSStorage();
  return netsRequest
    .get(`terminal/${terminalId || terminalID}`, {
      headers: {
        Authorization: `bearer ${token}`,
      },
    })
    .then((data) => data.data)
    .catch((err) => {
      throw err;
    });
};

const createTransaction = async (transaction) => {
  const [token, terminalID] = getConfigsFromSStorage();
  return axios
    .post(`${tempTestUrl}/v1/terminal/${terminalID}/transaction`, transaction, {
      headers: {
        Authorization: `bearer ${token}`,
      },
    })
    .then((data) => data.data)
    .catch(async (e) => {
      throw e;
    });
};

const voidLastTransaction = (transaction) => {
  const [token, terminalID] = getConfigsFromSStorage();
  return axios
    .delete(`${tempTestUrl}/v1/terminal/${terminalID}/transaction`, {
      headers: {
        Authorization: `bearer ${token}`,
      },
      data: transaction,
    })
    .then((data) => data)
    .catch((e) => {
      throw e;
    });
};

const referencedReturn = (transactionID, transaction) => {
  const [token, terminalID] = getConfigsFromSStorage();
  return axios
    .delete(
      `${tempTestUrl}/v1/terminal/${terminalID}/transaction/${transactionID}`,
      {
        headers: {
          Authorization: `bearer ${token}`,
        },
        data: transaction,
      },
    )
    .then((data) => data.data)
    .catch(async (e) => {
      throw e;
    });
};

const cancelOngoing = () => {
  const [token, terminalID] = getConfigsFromSStorage();
  return netsRequest.post(
    `terminal/${terminalID}/administration`,
    { action: 'cancelAction' },
    {
      headers: {
        Authorization: `bearer ${token}`,
      },
    },
  );
};

const downloadDataset = () => {
  const [token, terminalID] = getConfigsFromSStorage();
  return netsRequest.post(
    `terminal/${terminalID}/administration`,
    { action: 'downloadDataset' },
    {
      headers: {
        Authorization: `bearer ${token}`,
      },
    },
  );
};

const downloadSoftware = () => {
  const [token, terminalID] = getConfigsFromSStorage();
  return netsRequest.post(
    `terminal/${terminalID}/administration`,
    { action: 'downloadSoftware' },
    {
      headers: {
        Authorization: `bearer ${token}`,
      },
    },
  );
};

const reconciliation = () => {
  const [token, terminalID] = getConfigsFromSStorage();
  return netsRequest.post(
    `terminal/${terminalID}/administration`,
    { action: 'reconciliation' },
    {
      headers: {
        Authorization: `bearer ${token}`,
      },
    },
  );
};

const printLatestTransactionReceipt = () => {
  const [token, terminalID] = getConfigsFromSStorage();
  return netsRequest
    .post(
      `terminal/${terminalID}/administration`,
      { action: 'printLatestTransactionReceipt' },
      {
        headers: {
          Authorization: `bearer ${token}`,
        },
      },
    )
    .then((data) => data.data)
    .catch((e) => {
      throw e;
    });
};

const getTransaction = () => {
  const [token, terminalID] = getConfigsFromSStorage();
  return netsRequest
    .get(`terminal/${terminalID}/transaction`, {
      headers: {
        Authorization: `bearer ${token}`,
      },
    })
    .then((data) => data)
    .catch((e) => {
      console.error(e);
    });
};

/**
 * @typedef NetsTransaction
 * @property {"purchase"|string} transactionType
 * @property {Number} amount
 * @property {boolean} allowPinBypass
 */

export default {
  /**
   * Authentication and obtaining the token
   * @param {String} username nets fi username
   * @param {String} password nets fi password
   * @returns {Promise<{token: String, username: String, terminals: String[]}>}
   */
  login,
  /** Querying a list of allowed terminals */
  getTerminals: withAuthCheck(getTerminals),
  /**
   * Querying detailed terminal information
   * @param {String} terminalId terminal id
   */
  getTerminalInfo: withAuthCheck(getTerminalInfo),
  /**
   * Creating transactions (purchase, loyalty)
   * @param {NetsTransaction} transaction transaction object
   */
  createTransaction: withAuthCheck(createTransaction),
  /**
   * Void the latest transaction (reversal)
   * @param {{amount: String}} transaction object with amount of the last transaction in cents
   */
  voidLastTransaction: withAuthCheck(voidLastTransaction),
  /**
   * Refunding referenced transaction
   * @param {String} transactionID - transactionID
   * @param {{amount : Number}} transaction transaction object
   */
  referencedReturn: withAuthCheck(referencedReturn),
  /** Administrative action (reconciliation, canceling ongoing action, download software/dataset) */
  cancelOngoing: withAuthCheck(cancelOngoing),
  /** Getting the latest transaction result and receipt */
  getTransaction: withAuthCheck(getTransaction),
  /** Reconciliation a.k.a. Close batch, Close day */
  reconciliation: withAuthCheck(reconciliation),
  /** Download dataset */
  downloadDataset: withAuthCheck(downloadDataset),
  /** Download software to terminal */
  downloadSoftware: withAuthCheck(downloadSoftware),
  /** Print latest transaction receipt */
  printLatestTransactionReceipt: withAuthCheck(printLatestTransactionReceipt),
};
